



























































































































































































































































































































































































































































































































































































import { Component } from 'vue-property-decorator'
import axios from 'axios'
import _ from 'lodash'
import Breadcrumbs from '@/components/Breadcrumbs/index.vue'
import EntityMetadata from '@/components/EntityMetadata/index.vue'
import ItemList from '@/components/ItemList/index.vue'
import MembershipBadge from '@/components/MembershipBadge/index.vue'
import Page from '@/components/Page/index.vue'
import StatusFlash from '@/components/StatusFlash/index.vue'
import metadata from '@/utils/metadata'
import permissions from '@/utils/permissions'
import { parseSHACLView } from '@/components/ShaclForm/Parser/SHACLViewParser'
import EntityBase from '@/components/EntityBase'
import RdfLink from '@/components/RdfLink/index.vue'
import FairDataPoints from '@/components/FairDataPoints/index.vue'
import config from '@/config'

@Component({
  components: {
    FairDataPoints,
    Breadcrumbs,
    EntityMetadata,
    ItemList,
    MembershipBadge,
    Page,
    StatusFlash,
    RdfLink,
  },
})
export default class EntityView extends EntityBase {
  createLink: string = null

  extraLinks: any[] = []

  visualizationLink: any = null

  itemLists: any = null

  activeItemList: any = null

  meta: any = null

  metadata: any = null

  groups: any = null

  viewIndex: any = null

  datasetMetadata: any = null

  conceptAvailabilityMetadata: any = null

  codeFrequencyMetadata: any = null

  currentPage = 1

  totalPages = 0

  currentPageFreq = 1

  totalPagesFreq = 0

  datasetPagination = null

  pagination = null

  showCodeFrequency = false

  tableNotes = null

  get permissions() {
    return permissions
  }

  get canCreateChild() {
    return this.config.hasChildren
      && (this.isAdmin || this.config.canCreateChild(this.isAuthenticated, this.meta))
  }

  get isLandingPage() {
    return this.config.entitySpec.name === 'FAIR Data Point'
  }

  get isDraft() {
    return _.get(this.meta, 'state.current') === 'DRAFT'
  }

  get title() {
    if (this.viewIndex) return 'FAIR Data Points'
    return this.isDraft ? `[DRAFT] ${this.entity.title}` : this.entity.title
  }

  get isRepository() {
    return this.config.isRepository
  }

  get isIndex() {
    return config.isIndex()
  }

  get conceptAvailabilityColumns(): Record<string, boolean> {
    return this.columnsWithValues(
      this.conceptAvailabilityMetadata || [],
      ['patientCount', 'avgCount', 'minCount', 'maxCount', 'hospital'],
    )
  }

  get nestedCodeFrequencyColumns(): Map<any, Record<string, boolean>> {
    return new Map((this.conceptAvailabilityMetadata || []).map((row) => [
      row,
      this.columnsWithValues(row.codeFrequency || [], ['count', 'hospital']),
    ]))
  }

  get codeFrequencyColumns(): Record<string, Record<string, boolean>> {
    return _.fromPairs((this.codeFrequencyMetadata || []).map((standard) => [
      standard.key,
      this.columnsWithValues(standard.items, ['count', 'distinctCount', 'patientCount', 'hospital']),
    ]))
  }

  columnsWithValues(rows: any[], keys: string[]): Record<string, boolean> {
    const hasValue = (value) => value !== undefined && value !== null && value !== ''
    return _.fromPairs(keys.map((key) => [key, rows.some((row) => hasValue(row[key]))]))
  }

  actionEnabled(action: string): boolean {
    return _.includes(this.config.viewActions, action)
  }

  actionUrl(action: string): string {
    const { path } = this.$route
    return _.endsWith('/', path) ? `${path}${action}` : `${path}/${action}`
  }

  setActiveItemList(itemList) {
    this.activeItemList = itemList
  }

  setViewIndex(value) {
    this.viewIndex = value
  }

  reset() {
    this.metadata = null
    this.itemLists = null
    this.activeItemList = null
    this.meta = null
    this.extraLinks = []
    this.createLink = null
    this.visualizationLink = null
    this.groups = null
    this.datasetMetadata = null
    this.conceptAvailabilityMetadata = null
    this.codeFrequencyMetadata = null
    this.showCodeFrequency = false
    this.datasetPagination = null
    this.pagination = null
    this.tableNotes = null
    this.totalPages = 0
    this.currentPage = 1
    this.totalPagesFreq = 0
    this.currentPageFreq = 1
  }

  async fetchData(): Promise<void> {
    try {
      this.viewIndex = this.isIndex && this.isRepository

      this.status.setPending()
      const [entity, spec, meta] = await this.loadData()

      this.buildGraph(entity.data)
      this.groups = parseSHACLView(spec.data, this.config.targetClasses)
      this.meta = meta.data
      this.metadata = this.createMetadata()
      this.datasetMetadata = this.createDatasetMetadata()
      this.conceptAvailabilityMetadata = this.createConceptAvailabilityMetadata()
      this.codeFrequencyMetadata = this.createCodeFrequencyMetadata()
      this.tableNotes = {
        ATC: 'The codes and their associated counts are aggregated at the anatomical or pharmacological groups (1st level of ATC)',
        CHOP: 'The codes and their associated counts are aggregated at the Chapter level of CHOP.',
        'ICD-10-GM': 'The codes and their associated counts are aggregated at the Chapter level of ICD-10-GM.',
        'ICD-O-3': 'The codes and their associated counts are aggregated at the Chapter level of ICD-O-3.',
        LOINC: 'The codes and their associated counts are aggregated at the Class level of LOINC.',
        'SNOMED CT': 'The codes and associated counts are aggregated at the top-level concepts of SNOMED CT.',
      }
      this.extraLinks = this.config.getLinks(this.graph)
      // this.visualizationLink = this.config.getVisualizationLink(this.graph)
      this.breadcrumbs = this.config.createBreadcrumbs(this.meta.path, this.subject)

      if (this.config.hasChildren) {
        this.itemLists = this.config.createChildrenLists(this.canCreateChild, this.entityId)
        this.activeItemList = _.first(this.itemLists)
      }

      this.status.setDone()
    } catch (error) {
      this.status.setErrorFromResponse(error, 'Unable to get data.')
    }
  }

  async loadData() {
    return axios.all([
      this.config.api.get(this.entityId),
      this.config.api.getSpec(),
      this.config.api.getMeta(this.entityId),
    ])
  }

  createMetadata() {
    return [
      ...metadata.commonMetadata(this.graph),
      ...this.createLocalMetadata(),
    ]
  }

  createLocalMetadata() {
    return this.groups
      .map((group) => ({
        fields: group.fields
          .map((field) => metadata.fromShaclField(this.graph, field))
          .filter((field) => field !== null),
        label: group.label,
        comment: group.comment,
      }))
  }

  createDatasetMetadata() {
    const data = metadata.datasetMetadata(this.graph)
    this.datasetPagination = {
      classes: {
        current: 1,
        total: 0,
        filters: {
          name: {
            value: '',
            keys: ['concept'],
          },
        },
      },
      properties: {
        current: 1,
        total: 0,
        filters: {
          name: {
            value: '',
            keys: ['property'],
          },
        },
      },
    }
    return data
  }

  createConceptAvailabilityMetadata() {
    return [...metadata.conceptAvailabilityMetadata(this.graph)]
  }

  createCodeFrequencyMetadata() {
    let data = []
    const codeFrequencyList = metadata.codeFrequencyMetadata(this.graph)
    if (codeFrequencyList.length > 0) {
      const grouped = codeFrequencyList.reduce((acc, code) => {
        const key = code.standard
        if (!acc[key]) {
          acc[key] = []
        }
        acc[key].push(code)
        return acc
      }, {})
      const groupedList = Object.entries(grouped).map(([key, value]) => ({
        key,
        items: value,
      }))
      data = groupedList
    }
    data.sort((a, b) => a.key.localeCompare(b.key))
    this.pagination = data.reduce((acc, standard) => {
      acc[standard.key] = {
        current: 1,
        total: 0,
        filters: {
          label: { value: '', keys: ['label'] },
        },
      }
      return acc
    }, {})
    return data
  }

  async deleteEntity() {
    if (window.confirm(`Are you sure you want to delete ${this.entity.title}?`)) {
      try {
        await this.config.api.delete(this.entityId)
        const parent = _.get(_.last(this.breadcrumbs), 'to', '/')
        await this.$router.push(parent)
      } catch (err) {
        this.status.setError('Unable to delete data.')
      }
    }
  }

  async publishEntity() {
    if (window.confirm(`Are you sure you want to publish ${this.entity.title}?`)) {
      try {
        this.status.setPending()
        await this.config.api.putMetaState(this.entityId, { current: 'PUBLISHED' })
        await this.fetchData()
      } catch (err) {
        this.status.setError('Unable to publish data.')
      }
    }
  }
}
